import { Component } from '@theme/component';
import { RecentlyViewed } from '@theme/recently-viewed-products';

class RecentlyViewedSection extends Component {
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;
      observer.disconnect();
      this.#loadRecentlyViewed();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  connectedCallback() {
    super.connectedCallback();

    // Quick check: if no products in storage at all, stay hidden
    const products = RecentlyViewed.getProducts();
    const currentProductId = this.dataset.currentProductId;
    const hasOtherProducts = products.some((p) => p.id !== currentProductId);

    if (!hasOtherProducts) {
      return;
    }

    this.#intersectionObserver.observe(this);

    const prevBtn = this.querySelector('[data-recently-viewed-prev]');
    const nextBtn = this.querySelector('[data-recently-viewed-next]');
    const carousel = this.querySelector('[data-recently-viewed-grid]');

    if (prevBtn && nextBtn && carousel) {
      prevBtn.addEventListener('click', () => this.#scroll('prev'));
      nextBtn.addEventListener('click', () => this.#scroll('next'));
      carousel.addEventListener('scroll', () => this.#updateNavButtons(), {
        passive: true,
      });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#intersectionObserver.disconnect();
  }

  #scroll(direction) {
    const carousel = this.querySelector('[data-recently-viewed-grid]');
    if (!carousel) return;

    const slide = carousel.querySelector('.recently-viewed-slide');
    if (!slide) return;

    const slideWidth =
      slide.offsetWidth +
      parseInt(getComputedStyle(carousel).gap || '12', 10);
    const scrollAmount = direction === 'next' ? slideWidth : -slideWidth;

    carousel.scrollBy({ left: scrollAmount, behavior: 'smooth' });
  }

  #updateNavButtons() {
    const carousel = this.querySelector('[data-recently-viewed-grid]');
    const prevBtn = this.querySelector('[data-recently-viewed-prev]');
    const nextBtn = this.querySelector('[data-recently-viewed-next]');

    if (!carousel || !prevBtn || !nextBtn) return;

    prevBtn.disabled = carousel.scrollLeft <= 1;
    nextBtn.disabled =
      carousel.scrollLeft + carousel.clientWidth >= carousel.scrollWidth - 1;
  }

  /**
   * Escapes HTML special characters to prevent XSS.
   * @param {string} str
   * @returns {string}
   */
  #escapeHtml(str) {
    const div = document.createElement('div');
    div.textContent = str || '';
    return div.innerHTML;
  }

  /**
   * Builds a product card HTML string from product data.
   * @param {Object} product
   * @returns {string}
   */
  #buildCard(product) {
    const title = this.#escapeHtml(product.title);
    const price = this.#escapeHtml(product.price);
    const url = this.#escapeHtml(product.url);
    const image = this.#escapeHtml(product.image);

    const imageHtml = image
      ? `<img src="${image}" alt="${title}" width="300" height="400" loading="lazy">`
      : '';

    return `
      <div class="recently-viewed-slide">
        <a href="${url}" class="recently-viewed-card">
          <div class="recently-viewed-card__media">${imageHtml}</div>
          <div class="recently-viewed-card__info">
            <p class="recently-viewed-card__title">${title}</p>
            <p class="recently-viewed-card__price">${price}</p>
          </div>
        </a>
      </div>
    `;
  }

  /**
   * Fetches product data for a legacy entry (ID only, no cached data).
   * @param {string} handle
   * @returns {Promise<Object|null>}
   */
  async #fetchProduct(handle) {
    try {
      const res = await fetch(`/products/${handle}.js`);
      if (!res.ok) return null;
      const data = await res.json();
      // Format price: Shopify returns cents, convert to display format
      let priceStr = '';
      if (data.price > 0) {
        priceStr =
          typeof Shopify !== 'undefined' && Shopify.formatMoney
            ? Shopify.formatMoney(data.price)
            : (data.price / 100).toLocaleString(undefined, {
                style: 'currency',
                currency: data.currency || 'USD',
              });
      }
      return {
        id: String(data.id),
        handle: data.handle,
        title: data.title,
        url: `/products/${data.handle}`,
        price: priceStr,
        image: data.featured_image || '',
      };
    } catch {
      return null;
    }
  }

  async #loadRecentlyViewed() {
    const currentProductId = this.dataset.currentProductId;
    const maxProducts = parseInt(this.dataset.maxProducts, 10) || 4;

    let products = RecentlyViewed.getProducts();

    // Exclude current product
    if (currentProductId) {
      products = products.filter((p) => p.id !== currentProductId);
    }

    // Limit
    products = products.slice(0, maxProducts);

    if (products.length === 0) {
      return;
    }

    // For entries that have cached data (title, url, image), render immediately.
    // For legacy entries (ID only), try to fetch via handle if available.
    const resolvedProducts = await Promise.all(
      products.map(async (p) => {
        if (p.title && p.url && p.image) return p;
        if (p.handle) return this.#fetchProduct(p.handle);
        return null;
      })
    );

    const validProducts = resolvedProducts.filter(Boolean);

    const carousel = this.querySelector('[data-recently-viewed-grid]');
    if (!carousel || validProducts.length === 0) {
      return;
    }

    carousel.innerHTML = validProducts.map((p) => this.#buildCard(p)).join('');

    // Show the section now that we have content
    this.classList.remove('hidden');

    // Show nav arrows only if carousel overflows
    const nav = this.querySelector('[data-recently-viewed-nav]');
    if (nav) {
      const hasOverflow = carousel.scrollWidth > carousel.clientWidth;
      nav.style.display = hasOverflow ? '' : 'none';
    }

    this.#updateNavButtons();
  }
}

if (!customElements.get('recently-viewed-section')) {
  customElements.define('recently-viewed-section', RecentlyViewedSection);
}
